import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw, AlertTriangle, CheckCircle2, ExternalLink, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const HELP_TEXT = {
  news: "Missing body = no `long_summary` (the 'The Story So Far' H2 block on /news/:slug). Google sees a ~30 word snippet and flags Soft 404. LLM (Gemini 3 Flash) auto-fills every 60 min.",
  players: "Missing body = no `biography` (the H2 body on /players/:slug). Without it, only a stat card renders. LLM (Gemini 3 Flash) auto-fills every 60 min.",
  chants: "Missing body = no `long_context` (the 'About this chant' H2 block on /chants/:slug). ⚠ Admin-approval only — the scheduler will NOT auto-fill these. Click Enrich or Regenerate on each record manually to keep content accurate.",
  fan_memories: "Missing body = no content on /fan-memories/:slug. Manual edit required — these are user-authored.",
  fan_clubs: "Missing body = no `description` on /fan-clubs/:slug. ⚠ Admin-approval only — the scheduler will NOT auto-fill these because early v1 outputs contained boilerplate. Click Enrich to trigger LLM (v2 prompt now returns SKIP when it has nothing unique to say).",
  transfer_news: "This counts only admin-authored editorial pieces (created via /admin/transfers with a custom title + body). Scraped transfer stories that appear on /transfers are actually news articles indexed at /news/:slug — enrich those in the NEWS card above.",
  blogs: "Missing body = no blog content on /news/:slug. Manual edit from the News admin.",
};

const ADMIN_LINK = {
  news: "/admin/news",
  players: "/admin/players",
  chants: "/admin/chants",
  fan_memories: "/admin/fan-memories",
  fan_clubs: "/admin/fan-clubs",
  transfer_news: "/admin/transfers",
  blogs: "/admin/news",
};

export default function SeoHealthAdmin() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState({}); // { news: true }
  const [enrichingMeta, setEnrichingMeta] = useState({}); // { news: true }
  const [regenTemplated, setRegenTemplated] = useState({}); // { news: true }
  const [batchSize, setBatchSize] = useState({ news: 25, players: 25 });
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);

  const pingIndexNow = async () => {
    setPinging(true);
    setPingResult(null);
    try {
      const res = await fetch(`${API}/api/admin/seo/indexnow-sitemap`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json();
      setPingResult(data);
      if (data.skipped) {
        toast.warning(`IndexNow skipped: ${data.reason || "unknown"}`);
      } else if (data.ok) {
        toast.success(`IndexNow ✓ ${data.urls_submitted} URL(s) pushed to Bing / Yandex`);
      } else {
        toast.error(`IndexNow ping failed (status ${data.status || "n/a"})`);
      }
    } catch (e) {
      toast.error(`IndexNow ping failed: ${e.message}`);
    } finally {
      setPinging(false);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/seo/health`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReport(data.collections || []);
    } catch (e) {
      toast.error(`Failed to load SEO health: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const runEnrich = async (collection) => {
    const limit = Math.max(1, Math.min(500, Number(batchSize[collection] || 25)));
    // API paths use hyphens, DB collection names use underscores
    const apiSlug = collection.replace(/_/g, "-");
    setEnriching((s) => ({ ...s, [collection]: true }));
    try {
      const res = await fetch(`${API}/api/admin/seo/enrich-${apiSlug}?limit=${limit}`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.already_running) {
        toast.info(`A ${collection} backfill is already running (PID ${data.pid}). Refresh in a minute to see progress.`);
      } else {
        toast.success(
          `Backfill running in the background for up to ${limit} ${collection} — refresh the panel in ~1 min per 25 records to see counts drop.`
        );
      }
      // Give the subprocess a couple of seconds before the first health refresh
      setTimeout(loadReport, 3000);
    } catch (e) {
      toast.error(`Enrich failed: ${e.message}`);
    } finally {
      setEnriching((s) => ({ ...s, [collection]: false }));
    }
  };

  // Batch-generate the four SEO metadata fields (seo_title,
  // seo_description, seo_keywords, image_alt) via the Gemini 3 Flash
  // subprocess. Independent of the H2-body enricher above.
  const runEnrichMeta = async (collection) => {
    const limit = Math.max(1, Math.min(500, Number(batchSize[collection] || 25)));
    setEnrichingMeta((s) => ({ ...s, [collection]: true }));
    try {
      const res = await fetch(`${API}/api/admin/seo/enrich-meta/${collection}?limit=${limit}`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.already_running) {
        toast.info(`A ${collection} SEO meta backfill is already running (PID ${data.pid}). Refresh in a minute.`);
      } else {
        toast.success(
          `SEO meta backfill running for up to ${limit} ${collection} — refresh in ~30-90s to watch the "missing" counts drop.`
        );
      }
      setTimeout(loadReport, 3000);
    } catch (e) {
      toast.error(`Enrich meta failed: ${e.message}`);
    } finally {
      setEnrichingMeta((s) => ({ ...s, [collection]: false }));
    }
  };

  // Keyword-diversity guard — regenerate SEO meta ONLY for docs whose
  // keyword string is shared across multiple records ("templated"
  // output). Cheaper and more targeted than "Enrich all missing".
  const runRegenTemplated = async (collection) => {
    setRegenTemplated((s) => ({ ...s, [collection]: true }));
    try {
      const res = await fetch(`${API}/api/admin/seo/regenerate-templated/${collection}?max_docs=200`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.already_running) {
        toast.info(`A templated regen for ${collection} is already running (PID ${data.pid}).`);
      } else if (data.targeted_count === 0) {
        toast.success(`No templated clusters in ${collection} — nothing to regenerate.`);
      } else {
        toast.success(
          `Regenerating ${data.targeted_count} templated ${collection} — refresh in ~30-90s to watch the duplicate % drop.`
        );
      }
      setTimeout(loadReport, 3000);
    } catch (e) {
      toast.error(`Regenerate templated failed: ${e.message}`);
    } finally {
      setRegenTemplated((s) => ({ ...s, [collection]: false }));
    }
  };

  // Site-wide enrich — fires one background subprocess that walks EVERY
  // collection and backfills seo_* on every doc still missing them.
  // Cheaper for the admin (1 click) but the same total LLM cost as
  // clicking each per-collection button.
  const runEnrichAll = async () => {
    const perCollection = 200; // hard cap per collection this run
    setEnrichingMeta((s) => ({ ...s, __all__: true }));
    try {
      const res = await fetch(`${API}/api/admin/seo/enrich-meta/all?limit=${perCollection}`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.already_running) {
        toast.info(`A site-wide SEO meta backfill is already running (PID ${data.pid}). Refresh in a minute.`);
      } else {
        toast.success(
          `Site-wide SEO meta backfill started — up to ${perCollection} docs per collection. Refresh in ~1-3 minutes to watch every "missing" counter drop.`
        );
      }
      setTimeout(loadReport, 5000);
    } catch (e) {
      toast.error(`Enrich all failed: ${e.message}`);
    } finally {
      setEnrichingMeta((s) => ({ ...s, __all__: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="seo-health-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "Barlow Condensed" }}>
            SEO CONTENT HEALTH
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every indexable page has an H1 sourced from a DB field. This dashboard shows which records still lack the long-form body driving the H2 section (biography, long summary, lyrics, etc.). LLM-enrichable collections can be batch-filled with Gemini 3 Flash.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadReport}
            disabled={loading}
            data-testid="seo-health-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {/* One-click: enrich seo_* on every content collection at once. */}
          <Button
            onClick={runEnrichAll}
            disabled={enrichingMeta.__all__}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="seo-enrich-all-btn"
          >
            <Sparkles className={`w-4 h-4 mr-2 ${enrichingMeta.__all__ ? "animate-pulse" : ""}`} />
            {enrichingMeta.__all__ ? "Enriching all…" : "Enrich ALL missing SEO meta"}
          </Button>
          {/* Push every canonical sitemap URL to IndexNow (Bing / Yandex). */}
          <Button
            onClick={pingIndexNow}
            disabled={pinging}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="seo-indexnow-ping-btn"
          >
            <Zap className={`w-4 h-4 mr-2 ${pinging ? "animate-pulse" : ""}`} />
            {pinging ? "Pinging…" : "Ping IndexNow"}
          </Button>
        </div>
      </div>

      {pingResult && (
        <div
          className={`rounded-lg p-3 border text-sm ${
            pingResult.skipped
              ? "bg-yellow-50 border-yellow-300 text-yellow-800"
              : pingResult.ok
              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
              : "bg-red-50 border-red-300 text-red-800"
          }`}
          data-testid="seo-indexnow-result"
        >
          {pingResult.skipped ? (
            <>IndexNow skipped — {pingResult.reason === "no_key" ? "INDEXNOW_KEY env var not set (safe on preview, must be set in production)." : pingResult.reason}</>
          ) : pingResult.ok ? (
            <>Pushed <strong>{pingResult.urls_submitted}</strong> canonical URL(s) to IndexNow. Bing / Yandex / DuckDuckGo typically recrawl within minutes.</>
          ) : (
            <>Ping failed — HTTP {pingResult.status || "n/a"}. Check the backend logs.</>
          )}
        </div>
      )}

      {loading && report.length === 0 ? (
        <div className="text-slate-500">Loading SEO audit…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {report.map((row) => {
            const pctBody =
              row.total_indexable > 0
                ? Math.round(((row.total_indexable - row.missing_body) / row.total_indexable) * 100)
                : 100;
            const isHealthy = row.missing_h1 === 0 && row.missing_body === 0;
            const canEnrich = row.llm_enrichable && row.missing_body > 0;
            return (
              <Card
                key={row.collection}
                className="overflow-hidden"
                data-testid={`seo-card-${row.collection}`}
              >
                <CardHeader className="bg-slate-50">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <span className="uppercase tracking-wide">
                      {row.collection.replace("_", " ")}
                    </span>
                    {isHealthy ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> healthy
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                        <AlertTriangle className="w-3 h-3 mr-1" /> {row.missing_body} to fix
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-2xl font-bold" data-testid={`seo-total-${row.collection}`}>
                        {row.total_indexable}
                      </div>
                      <div className="text-xs text-slate-500">indexable</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-700">
                        {row.total_indexable - row.missing_body}
                      </div>
                      <div className="text-xs text-slate-500">have body ({pctBody}%)</div>
                    </div>
                    <div>
                      <div
                        className={`text-2xl font-bold ${
                          row.missing_body > 0 ? "text-amber-700" : "text-emerald-700"
                        }`}
                        data-testid={`seo-missing-${row.collection}`}
                      >
                        {row.missing_body}
                      </div>
                      <div className="text-xs text-slate-500">missing body</div>
                    </div>
                  </div>

                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        pctBody === 100
                          ? "bg-emerald-500"
                          : pctBody > 60
                            ? "bg-amber-500"
                            : "bg-red-500"
                      }`}
                      style={{ width: `${pctBody}%` }}
                    />
                  </div>

                  <p className="text-xs text-slate-500 leading-relaxed">
                    <strong>H1 source:</strong> <code>{row.h1_field}</code> ·{" "}
                    <strong>Body source:</strong> <code>{row.body_field}</code>
                    <br />
                    {HELP_TEXT[row.collection]}
                  </p>

                  {/* SEO metadata coverage — seo_title / seo_description /
                      seo_keywords / image_alt. Missing any of these = weaker
                      search signal and risk of Soft 404 on Bing/DuckDuckGo. */}
                  <div className="border-t pt-3 mt-2" data-testid={`seo-meta-block-${row.collection}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        SEO metadata
                      </span>
                      {row.missing_seo_title === 0
                        && row.missing_seo_description === 0
                        && row.missing_seo_keywords === 0
                        && row.missing_image_alt === 0 ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> all set
                        </Badge>
                      ) : (
                        <Badge className="bg-violet-100 text-violet-800 border-violet-200">
                          {Math.max(
                            row.missing_seo_title,
                            row.missing_seo_description,
                            row.missing_seo_keywords,
                            row.missing_image_alt
                          )} to generate
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center text-xs mb-2">
                      <div title="Missing <title> override">
                        <div className={`font-bold ${row.missing_seo_title ? "text-violet-700" : "text-emerald-700"}`}
                             data-testid={`seo-meta-missing-title-${row.collection}`}>
                          {row.missing_seo_title}
                        </div>
                        <div className="text-slate-500">title</div>
                      </div>
                      <div title="Missing meta description">
                        <div className={`font-bold ${row.missing_seo_description ? "text-violet-700" : "text-emerald-700"}`}
                             data-testid={`seo-meta-missing-desc-${row.collection}`}>
                          {row.missing_seo_description}
                        </div>
                        <div className="text-slate-500">desc</div>
                      </div>
                      <div title="Missing keywords">
                        <div className={`font-bold ${row.missing_seo_keywords ? "text-violet-700" : "text-emerald-700"}`}
                             data-testid={`seo-meta-missing-kw-${row.collection}`}>
                          {row.missing_seo_keywords}
                        </div>
                        <div className="text-slate-500">keywords</div>
                      </div>
                      <div title="Missing image alt text">
                        <div className={`font-bold ${row.missing_image_alt ? "text-violet-700" : "text-emerald-700"}`}
                             data-testid={`seo-meta-missing-alt-${row.collection}`}>
                          {row.missing_image_alt}
                        </div>
                        <div className="text-slate-500">alt</div>
                      </div>
                    </div>
                    {(row.missing_seo_title || row.missing_seo_description
                        || row.missing_seo_keywords || row.missing_image_alt) ? (
                      <Button
                        onClick={() => runEnrichMeta(row.collection)}
                        disabled={enrichingMeta[row.collection]}
                        variant="outline"
                        className="w-full border-violet-300 text-violet-700 hover:bg-violet-50 h-9"
                        data-testid={`seo-enrich-meta-btn-${row.collection}`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {enrichingMeta[row.collection]
                          ? "Generating meta…"
                          : `Enrich SEO meta (next ${batchSize[row.collection] ?? 25})`}
                      </Button>
                    ) : (
                      <p className="text-xs text-emerald-700 flex items-center">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Full SEO metadata on every record
                      </p>
                    )}

                    {/* Keyword-diversity guard — flagged when >20% of docs
                        share the exact same keyword string ("templated"
                        LLM output). Shows a warning banner + a targeted
                        "regenerate ONLY the templated docs" button so
                        admins can heal cheaply without re-hitting every
                        record in the collection. */}
                    {row.keyword_diversity?.flagged && (
                      <div
                        className="mt-3 p-3 rounded-md border-2 border-amber-300 bg-amber-50"
                        data-testid={`kw-diversity-warning-${row.collection}`}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-amber-900 leading-snug">
                            <strong>{row.keyword_diversity.duplicate_pct}%</strong> of{" "}
                            {row.collection} share duplicate keywords with another record
                            ({row.keyword_diversity.templated_count} of {row.total_indexable}).
                            The largest cluster of{" "}
                            <strong>{row.keyword_diversity.top_cluster_size}</strong> docs uses:{" "}
                            <code className="text-[10px] bg-amber-100 px-1 py-0.5 rounded break-all">
                              {row.keyword_diversity.top_cluster_keywords}
                            </code>
                          </div>
                        </div>
                        <Button
                          onClick={() => runRegenTemplated(row.collection)}
                          disabled={regenTemplated[row.collection]}
                          variant="outline"
                          size="sm"
                          className="w-full border-amber-400 bg-white text-amber-900 hover:bg-amber-100 h-8 text-xs"
                          data-testid={`kw-diversity-regen-btn-${row.collection}`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          {regenTemplated[row.collection]
                            ? "Regenerating…"
                            : `Regenerate ${Math.min(row.keyword_diversity.templated_count, 200)} templated records`}
                        </Button>
                      </div>
                    )}
                    {row.keyword_diversity
                      && !row.keyword_diversity.flagged
                      && row.keyword_diversity.templated_count > 0 && (
                      <p
                        className="mt-2 text-[11px] text-slate-500"
                        data-testid={`kw-diversity-ok-${row.collection}`}
                      >
                        Keyword diversity: {row.keyword_diversity.duplicate_pct}% duplicates
                        (below {row.keyword_diversity.threshold_pct}% threshold)
                      </p>
                    )}
                  </div>

                  {row.collection === "transfer_news" && (
                    <div className="text-xs bg-sky-50 border border-sky-200 text-sky-900 p-2 rounded">
                      <strong>ℹ Note:</strong> {row.scraped_transfer_news_via_news ?? 0} scraped
                      transfer stories currently show on <code>/transfers</code> — they route to{" "}
                      <code>/news/:slug</code> and are enriched via the <strong>NEWS</strong> card
                      above, not here. This card only counts admin-authored editorial pieces
                      created via <code>/admin/transfers</code>.
                    </div>
                  )}

                  {row.missing_h1 > 0 && (
                    <div className="text-xs text-red-700 bg-red-50 p-2 rounded">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      {row.missing_h1} record(s) have NO H1 source — these shells will render an empty
                      &lt;h1&gt;. Open the admin below to fix.
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                    {canEnrich ? (
                      <>
                        <Input
                          type="number"
                          min={1}
                          max={200}
                          value={batchSize[row.collection] ?? 25}
                          onChange={(e) =>
                            setBatchSize((s) => ({
                              ...s,
                              [row.collection]: Number(e.target.value),
                            }))
                          }
                          className="w-20 h-9"
                          data-testid={`seo-batch-input-${row.collection}`}
                        />
                        <Button
                          onClick={() => runEnrich(row.collection)}
                          disabled={enriching[row.collection]}
                          className="bg-[#7C2C3B] hover:bg-[#6B2534] text-white"
                          data-testid={`seo-enrich-btn-${row.collection}`}
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          {enriching[row.collection] ? "Enriching…" : `Enrich next ${batchSize[row.collection] ?? 25}`}
                        </Button>
                      </>
                    ) : row.missing_body > 0 ? (
                      <a
                        href={ADMIN_LINK[row.collection] || "#"}
                        className="text-sm text-[#7C2C3B] hover:underline flex items-center"
                        data-testid={`seo-open-admin-${row.collection}`}
                      >
                        Open {row.collection} admin to fix manually
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    ) : (
                      <span className="text-sm text-emerald-700 flex items-center">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> All records have body text
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="bg-slate-50">
        <CardContent className="pt-4 text-xs text-slate-600 space-y-1">
          <p>
            <strong>Automation:</strong> The <code>llm_content_backfill_scheduler</code> runs every
            30 min and processes up to 60 news + 30 players per tick. Use the buttons above only for
            on-demand top-ups (e.g. after a fresh scrape burst).
          </p>
          <p>
            <strong>H1 coverage:</strong> The build-time <code>postbuild-seo.js</code> writes one
            static shell per slug&apos;d DB record, always emitting <code>&lt;h1&gt;{"{name|title|headline}"}&lt;/h1&gt;</code>. If a source field is empty (see red banner above), the shell renders an empty H1.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
